import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import TextBlock from '@component/common/block/text-block';
import Gap from '@component/common/gap';
import IconBlock from '@component/common/block/icon-block';
import { loadKakaoMaps } from '@util/kakao-map';
import * as color from '@constant/color';
import * as size from '@constant/size';

interface IProps {
  latitude?: number;
  longitude?: number;
  handleExpandClick?: () => void;
}

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background-color: ${color.GRAY_4};
`;
const MapContainer = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
`;

const PlaceholderContainer = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${size.MARGIN_1};
  background-color: ${color.GRAY_4};
`;

const ExpandButtonContainer = styled.div`
  position: absolute;
  bottom: calc(${size.MARGIN_1} / 2);
  right: calc(${size.MARGIN_1} / 2);
  z-index: 1;
`;

const MapThumbnail: React.FC<IProps> = ({ latitude, longitude, handleExpandClick }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const valid =
    typeof latitude === 'number' &&
    typeof longitude === 'number' &&
    Number.isFinite(latitude) &&
    Number.isFinite(longitude) &&
    Math.abs(latitude) <= 90 &&
    Math.abs(longitude) <= 180;

  useEffect(() => {
    if (!valid || !wrapperRef.current) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setVisible(true);
        observer.disconnect();
      }
    });
    observer.observe(wrapperRef.current);
    return () => observer.disconnect();
  }, [valid]);

  useEffect(() => {
    if (!visible || !valid || latitude === undefined || longitude === undefined) return;
    let active = true;
    let cleanup = () => {};
    setReady(false);
    setFailed(false);
    loadKakaoMaps()
      .then((maps) => {
        const element = mapRef.current;
        if (!active || !element) return;
        const center = new maps.LatLng(latitude, longitude);
        const map = new maps.Map(element, {
          center,
          level: 11,
          draggable: false,
          scrollwheel: false,
          disableDoubleClickZoom: true,
          keyboardShortcuts: false,
        });
        const marker = new maps.Marker({ position: center, map, draggable: false });
        const observer = new ResizeObserver(() => {
          map.relayout();
          map.setCenter(center);
        });
        observer.observe(element);
        cleanup = () => {
          observer.disconnect();
          marker.setMap(null);
          element.replaceChildren();
        };
        setReady(true);
      })
      .catch(() => {
        if (active) {
          setReady(false);
          setFailed(true);
        }
      });
    return () => {
      active = false;
      cleanup();
    };
  }, [visible, valid, latitude, longitude]);

  return (
    <Wrapper ref={wrapperRef}>
      {valid && <MapContainer ref={mapRef} />}
      {(!valid || !ready) && (
        <PlaceholderContainer>
          <IconBlock
            icon={valid ? 'map-pin' : 'map-pin-off'}
            design={{ icon: { size: 'medium', color: color.GRAY_1 } }}
          />
          <Gap height='5px' />
          <TextBlock
            text={!valid ? '위치 미등록' : failed ? '지도를 불러오지 못했어요' : '지도 로딩 중'}
            design={{ text: { size: 'small', color: color.GRAY_1 } }}
          />
        </PlaceholderContainer>
      )}
      {valid && handleExpandClick && (
        <ExpandButtonContainer onClick={(event) => event.stopPropagation()}>
          <IconBlock
            icon='maximize'
            design={{
              box: {
                width: size.DESIGN_SYSTEM_BUTTON_SMALL_HEIGHT,
                height: size.DESIGN_SYSTEM_BUTTON_SMALL_HEIGHT,
                backgroundColor: color.WHITE_1,
                borderRadius: size.DESIGN_SYSTEM_BUTTON_BORDER_RADIUS,
                shadow: color.SURFACE_SHADOW,
                event: { cursor: 'pointer' },
              },
              icon: { size: 'tiny', kind: 'outlined', color: color.BLACK_2 },
            }}
            click={{ handler: handleExpandClick }}
          />
        </ExpandButtonContainer>
      )}
    </Wrapper>
  );
};
export default MapThumbnail;
